import { motion } from "framer-motion";
import { useEffect, useMemo, useRef } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import trpc from "../utils/trpc.js";
import { Button } from "./button/button";
import useLearningState from "../hooks/use-learning-state.js";
import useCurrentModuleItemId from "../hooks/use-current-module-item-id.js";
import { ArrowLongLeftIcon } from "@heroicons/react/24/outline";
import { MapIcon, UserIcon } from "@heroicons/react/24/solid";
import FloatingChat from "./chat/floating-chat.js";
import { ChatProvider } from "../providers/chat.js";

export default function MainLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: sessionData } = trpc.session.get.useQuery(undefined, { staleTime: Infinity });
  const { data: courseSettings } = trpc.course.getSettings.useQuery(undefined, { staleTime: Infinity });
  const displayName = sessionData?.userInfo.name;
  const { hasStarted } = useLearningState();
  const currentModuleItemId = useCurrentModuleItemId();
  const isModuleItem = Boolean(currentModuleItemId);
  const welcomeMessage = hasStarted ? "Welcome back," : "Welcome,";

  // Track previous state to determine if we should animate
  const prevStateRef = useRef<{ isModuleItem: boolean; pathname: string } | null>(null);
  const navRef = useRef<HTMLDivElement>(null);

  // Animate on: tab switches (My Journey ↔ Leadership Profile) or entering/leaving a learning item
  // Don't animate: between learning items
  const shouldAnimate = useMemo(() => {
    const prev = prevStateRef.current;
    if (!prev) return true; // First render
    if (!prev.isModuleItem && !isModuleItem && prev.pathname !== location.pathname) return true; // Tab switch
    if (!prev.isModuleItem && isModuleItem) return true; // Entering a learning item
    if (prev.isModuleItem && !isModuleItem) return true; // Leaving a learning item (back button)
    return false; // Navigating between learning items
  }, [isModuleItem, location.pathname]);

  useEffect(() => {
    prevStateRef.current = { isModuleItem, pathname: location.pathname };
  }, [isModuleItem, location.pathname]);

  useEffect(() => {
    if (navRef.current) {
      const active = navRef.current.querySelector('[aria-current="page"]') as HTMLElement | null;
      active?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
    }
  }, [location.pathname]);

  const { data: modules } = trpc.course.getModules.useQuery(undefined, {
    staleTime: Infinity,
    enabled: isModuleItem,
  });

  const { moduleName, subModuleName } = useMemo(() => {
    if (!modules || !currentModuleItemId) return { moduleName: null, subModuleName: null };
    for (const module of modules) {
      for (const sub of module.subModules) {
        const found = sub.items.some(item => String(item.id) === currentModuleItemId);
        if (found) {
          return { moduleName: module.name, subModuleName: sub.title };
        }
      }
    }
    return { moduleName: null, subModuleName: null };
  }, [modules, currentModuleItemId]);

  return (
    <ChatProvider>
      <div className="relative mx-auto max-w-screen-xl space-y-8 px-6 pb-24 pt-12 lg:px-8">
        <header
          className={`relative flex flex-col gap-6 pt-2 ${
            isModuleItem ? "md:flex-row md:items-center md:justify-between" : ""
          }`}>
          {isModuleItem ? (
            <>
              <div className="flex flex-col gap-4 self-start md:flex-row md:items-center md:self-auto">
                <Button onClick={() => navigate("/")} className="group shrink-0 self-start px-4 py-2 md:self-auto">
                  <span className="flex items-center gap-2">
                    <ArrowLongLeftIcon className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
                    Back
                  </span>
                </Button>
                {moduleName && (
                  <div className="gap-2 text-sm">
                    <span className="mb-1 text-2xl font-semibold">{moduleName}</span>
                    <div>{subModuleName}</div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex w-full flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="space-y-3">
                  <p className="text-sm font-semibold uppercase tracking-[0.1em]">
                    {courseSettings?.courseImageUrl ? (
                      <img
                        src={courseSettings.courseImageUrl}
                        alt={sessionData?.courseInfo.courseTitle}
                        className="h-4 w-auto"
                      />
                    ) : (
                      sessionData?.courseInfo.courseTitle
                    )}
                  </p>
                  <h1 className="text-2xl font-semibold md:text-3xl">
                    {welcomeMessage} {displayName}
                  </h1>
                </div>
              </div>
              <nav className="mt-4 w-full">
                <div
                  ref={navRef}
                  className="scrollbar-hide -mx-2 flex flex-nowrap gap-6 overflow-x-auto border-b border-gray px-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {[
                    {
                      name: (
                        <span className="inline-flex items-center gap-1.5">
                          {courseSettings?.journeyTabName ?? "My Journey"}
                          <MapIcon className="h-4 w-4" />
                        </span>
                      ),
                      path: "/",
                      disabled: !(courseSettings?.journeyTabEnabled ?? true),
                    },
                    {
                      name: (
                        <span className="inline-flex items-center gap-1.5">
                          {courseSettings?.profileTabName ?? "My Profile"}
                          <UserIcon className="h-4 w-4" />
                        </span>
                      ),
                      path: "/profile",
                      disabled: false,
                    },
                  ].map(tab =>
                    tab.disabled ? (
                      <span
                        key={tab.path}
                        className="relative cursor-not-allowed select-none whitespace-nowrap pb-3 text-sm font-semibold text-gray-40">
                        {tab.name}
                      </span>
                    ) : (
                      <NavLink
                        key={tab.path}
                        to={tab.path}
                        end
                        className={({ isActive }) =>
                          `relative -mx-2 whitespace-nowrap rounded px-2 pb-3 pt-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lac-leman focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                            isActive ? "text-lac-leman" : "text-gray-60 hover:text-gray-80"
                          }`
                        }>
                        {({ isActive }) => (
                          <>
                            {tab.name}
                            {isActive ? (
                              <motion.div
                                layoutId="tab-underline"
                                className="absolute bottom-0 left-0 h-0.5 w-full bg-lac-leman"
                                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                              />
                            ) : null}
                          </>
                        )}
                      </NavLink>
                    ),
                  )}
                </div>
              </nav>
            </>
          )}
        </header>

        {isModuleItem ? <div className="h-px w-full bg-gray" /> : null}

        <main>
          <motion.div
            key={`${location.pathname}${location.search}`}
            initial={shouldAnimate ? { opacity: 0, y: 8 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}>
            <Outlet />
          </motion.div>
        </main>
        <FloatingChat />
      </div>
    </ChatProvider>
  );
}
